import {
  type ApolloClient,
  CombinedGraphQLErrors,
  type TypedDocumentNode,
} from '@apollo/client';
import {
  AdminFeatureGrantCleanupLocationsDocument,
  AdminFeatureGrantCleanupStudiosDocument,
  AdminFeatureGrantCleanupUsersDocument,
  AdminListFeatureGrantsDocument,
  AdminManyFeaturesDocument,
  FeatureGrantScopeType,
} from '../../../graphql/types';
import type {
  CleanupFeature,
  CleanupGrant,
  CleanupLocation,
  CleanupStudio,
  CleanupUser,
} from './analyzeFeatureGrants';

// The backend's ceiling. A larger `first` isn't clamped to it — it's discarded
// for a default of 25.
const PAGE_SIZE = 100;
// Precedence only replays correctly against a client's whole grant set, so past
// this the analysis refuses rather than judging half the picture.
const MAX_GRANTS = 5000;
const MANY_CHUNK_SIZE = 50;

const tooManyGrants = (featureFiltered: boolean) =>
  new Error(
    featureFiltered
      ? `This client has more than ${MAX_GRANTS} grants for that feature, which is more than the analysis can take in at once.`
      : `This client has more than ${MAX_GRANTS} grants. Pick a single feature to narrow the analysis.`,
  );

export interface CleanupData {
  grants: CleanupGrant[];
  /** Both already on the grant connection — no extra round trip for either. */
  clientName: string;
  totalGrants: number;
  features: Map<string, CleanupFeature>;
  users: Map<string, CleanupUser>;
  locations: Map<string, CleanupLocation>;
  studios: Map<string, CleanupStudio>;
}

type Client = ApolloClient;

const byId = <TNode extends { id: string }, TOut>(
  nodes: TNode[],
  map: (node: TNode) => TOut,
) => new Map(nodes.map((node) => [node.id, map(node)]));

const uniqueScopeIds = (
  grants: CleanupGrant[],
  scopeType: FeatureGrantScopeType,
) => [
  ...new Set(
    grants
      .filter((grant) => grant.scopeType === scopeType)
      .map((grant) => grant.scopeId),
  ),
];

/**
 * The `manyX` queries fail the whole batch with a GraphQL error when one id no
 * longer resolves, so a failed batch is halved until the bad ids are isolated
 * and left out.
 */
const loadMany = async <TQuery, TNode>(
  client: Client,
  query: TypedDocumentNode<TQuery, { ids: string[] }>,
  ids: string[],
  select: (data: TQuery) => TNode[],
): Promise<TNode[]> => {
  const loaded: TNode[] = [];

  const run = async (batch: string[]): Promise<void> => {
    if (batch.length === 0) {
      return;
    }
    try {
      const { data } = await client.query({
        query,
        variables: { ids: batch },
        fetchPolicy: 'network-only',
        errorPolicy: 'none',
      });
      loaded.push(...select(data));
    } catch (error) {
      // Throw on anything that isn't a GraphQL error. Only a GraphQL error
      // means a bad id; a network or server error would fail every half and
      // drop them all.
      if (!CombinedGraphQLErrors.is(error)) {
        throw error;
      }
      if (batch.length === 1) {
        return;
      }
      const mid = Math.floor(batch.length / 2);
      await run(batch.slice(0, mid));
      await run(batch.slice(mid));
    }
  };

  for (let i = 0; i < ids.length; i += MANY_CHUNK_SIZE) {
    await run(ids.slice(i, i + MANY_CHUNK_SIZE));
  }
  return loaded;
};

const loadAllGrants = async (
  client: Client,
  filter: { clientId: string; featureId?: string },
): Promise<{ grants: CleanupGrant[]; clientName: string; total: number }> => {
  const grants: CleanupGrant[] = [];
  let clientName = '';
  let total = 0;
  let after: string | undefined;
  let hasNextPage = false;

  do {
    const { data } = await client.query({
      query: AdminListFeatureGrantsDocument,
      variables: { input: { first: PAGE_SIZE, after }, filter },
      fetchPolicy: 'network-only',
      errorPolicy: 'none',
    });
    total = data.data.count;
    clientName = data.data.nodes[0]?.client.name ?? clientName;
    grants.push(
      ...data.data.nodes.map((node) => ({
        id: node.id,
        clientId: node.client.id,
        featureId: node.feature.id,
        featureKey: node.feature.key,
        scopeType: node.scopeType,
        scopeId: node.scopeId,
        state: node.state,
      })),
    );
    // `count` is the whole filtered total, so an oversized set is refused on
    // page one rather than after paging to the cap.
    if (data.data.count > MAX_GRANTS || grants.length > MAX_GRANTS) {
      throw tooManyGrants(!!filter.featureId);
    }
    hasNextPage = data.data.pageInfo.hasNextPage;
    after = data.data.pageInfo.endCursor ?? undefined;
  } while (hasNextPage && after);

  return { grants, clientName, total };
};

export const loadCleanupData = async (
  client: Client,
  filter: { clientId: string; featureId?: string },
): Promise<CleanupData> => {
  const { grants, clientName, total } = await loadAllGrants(client, filter);

  const featureIds = [...new Set(grants.map((grant) => grant.featureId))];
  const [features, users, locations, studios] = await Promise.all([
    loadMany(
      client,
      AdminManyFeaturesDocument,
      featureIds,
      (data) => data.data,
    ),
    loadMany(
      client,
      AdminFeatureGrantCleanupUsersDocument,
      uniqueScopeIds(grants, FeatureGrantScopeType.User),
      (data) => data.manyUsers,
    ),
    loadMany(
      client,
      AdminFeatureGrantCleanupLocationsDocument,
      uniqueScopeIds(grants, FeatureGrantScopeType.Location),
      (data) => data.manyCoachLocations,
    ),
    loadMany(
      client,
      AdminFeatureGrantCleanupStudiosDocument,
      uniqueScopeIds(grants, FeatureGrantScopeType.Studio),
      (data) => data.manyStudios,
    ),
  ]);

  return {
    grants,
    clientName: clientName || filter.clientId,
    totalGrants: total,
    features: byId(features, (feature) => ({
      id: feature.id,
      key: feature.key,
      defaultState: feature.defaultState,
      isArchived: feature.isArchived,
    })),
    users: byId(users, (user) => ({
      id: user.id,
      email: user.email,
      clientId: user.fitClient?.id ?? null,
      countryCode: user.countryCode ?? null,
      locationId: user.officeLocation?.id ?? null,
      studioId: user.officeLocation?.studio?.id ?? null,
    })),
    locations: byId(locations, (location) => ({
      id: location.id,
      name: location.name ?? null,
      studioId: location.studio?.id ?? null,
      clientId: location.studio?.client?.id ?? null,
    })),
    studios: byId(studios, (studio) => ({
      id: studio.id,
      name: studio.name ?? null,
      clientId: studio.client?.id ?? null,
    })),
  };
};
